import { useSendNotification } from "@app/hooks/useNotification";
import {
  useAgentTriggers,
  useDeleteTrigger,
} from "@app/lib/swr/agent_triggers";
import { describeScheduleConfig } from "@app/lib/utils/schedule_description";
import type { LightAgentConfigurationType } from "@app/types/assistant/agent";
import type { TriggerType } from "@app/types/assistant/triggers";
import { assertNever } from "@app/types/shared/utils/assert_never";
import type { WorkspaceType } from "@app/types/user";
import {
  ActionCard,
  Bell01,
  Button,
  CardGrid,
  Clock,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Plus,
  Spinner,
} from "@dust-tt/sparkle";
import i18n from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function getTriggerDescription(trigger: TriggerType): string {
  switch (trigger.kind) {
    case "schedule":
      try {
        return i18n.t("assistant.triggerRunsSchedule", "Runs {{schedule}}.", {
          schedule: describeScheduleConfig(trigger.configuration),
        });
      } catch {
        return "";
      }
    case "webhook":
      return trigger.configuration.event
        ? i18n.t(
            "assistant.triggerByEvents",
            "Triggered by {{event}} events.",
            { event: trigger.configuration.event }
          )
        : i18n.t(
            "assistant.triggerByWebhookEvents",
            "Triggered by webhook events."
          );
    default:
      assertNever(trigger);
  }
}

function getTriggerIcon(trigger: TriggerType) {
  switch (trigger.kind) {
    case "schedule":
      return Clock;
    case "webhook":
      return Bell01;
    default:
      assertNever(trigger);
  }
}

interface AgentTriggersTabProps {
  agentConfiguration: LightAgentConfigurationType;
  owner: WorkspaceType;
  onEditTrigger: (trigger: TriggerType) => void;
  onAddTrigger: () => void;
}

export function AgentTriggersTab({
  agentConfiguration,
  owner,
  onEditTrigger,
  onAddTrigger,
}: AgentTriggersTabProps) {
  const { triggers, isTriggersLoading } = useAgentTriggers({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration.sId,
  });

  const { t } = useTranslation();
  const [triggerToDelete, setTriggerToDelete] = useState<TriggerType | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const sendNotification = useSendNotification();

  const deleteTrigger = useDeleteTrigger({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration.sId,
  });

  const handleDeleteTrigger = async () => {
    if (!triggerToDelete) {
      return;
    }
    setIsDeleting(true);
    const success = await deleteTrigger(triggerToDelete.sId);
    setIsDeleting(false);
    setTriggerToDelete(null);

    if (success) {
      sendNotification({
        type: "success",
        title: t("assistant.triggerDeletedTitle", "Trigger deleted"),
        description: t(
          "assistant.triggerDeletedDescription",
          'The trigger "{{name}}" has been deleted.',
          { name: triggerToDelete.name }
        ),
      });
    } else {
      sendNotification({
        type: "error",
        title: t(
          "assistant.triggerDeleteErrorTitle",
          "Failed to delete trigger"
        ),
        description: t(
          "assistant.triggerDeleteErrorDescription",
          "An error occurred while deleting the trigger."
        ),
      });
    }
  };

  // TODO(adrien): for now, we only show the user's triggers.
  // We might reconsider it, and display a "My triggers" section,
  // and a "How others automate this" section in the future.
  const filteredTriggers = useMemo(
    () => triggers.filter((trigger) => trigger.isEditor),
    [triggers]
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t("assistant.myTriggers", "My triggers")}
        </h3>
        <Button
          label={t("assistant.addTrigger", "Add trigger")}
          icon={Plus}
          variant="outline"
          size="sm"
          onClick={onAddTrigger}
        />
      </div>

      {isTriggersLoading ? (
        <div className="w-full p-6">
          <Spinner variant="dark" />
        </div>
      ) : filteredTriggers.length === 0 ? (
        <div className="text-muted-foreground text-sm">
          {t(
            "common.noTriggersYet",
            "You have no triggers set up for this agent yet."
          )}
        </div>
      ) : (
        <CardGrid>
          {filteredTriggers.map((trigger) => (
            <ActionCard
              key={trigger.sId}
              icon={getTriggerIcon(trigger)}
              label={trigger.name}
              description={getTriggerDescription(trigger)}
              canAdd={false}
              disabled={trigger.status !== "enabled"}
              onClick={() => onEditTrigger(trigger)}
              onRemove={() => setTriggerToDelete(trigger)}
              cardContainerClassName="min-h-28"
            />
          ))}
        </CardGrid>
      )}

      <Dialog
        open={triggerToDelete !== null}
        onOpenChange={(open) => !open && setTriggerToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("assistant.deleteTriggerTitle", "Delete trigger")}
            </DialogTitle>
            <DialogDescription>
              {t("common.deleteTriggerConfirm", {
                defaultValue:
                  'Are you sure you want to delete the trigger "{{name}}"?',
                name: triggerToDelete?.name ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          {isDeleting ? (
            <div className="flex justify-center py-8">
              <Spinner variant="dark" size="md" />
            </div>
          ) : (
            <>
              <DialogContainer>
                <b>
                  {t(
                    "common.actionCannotBeUndone",
                    "This action cannot be undone."
                  )}
                </b>
              </DialogContainer>
              <DialogFooter
                leftButtonProps={{
                  label: t("assistant.cancel", "Cancel"),
                  variant: "outline",
                }}
                rightButtonProps={{
                  label: t("assistant.delete", "Delete"),
                  variant: "warning",
                  onClick: handleDeleteTrigger,
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
